import { useEffect, useState, useCallback } from 'react'
import { coursesAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import {
  Plus, Pencil, Trash2, Loader2, X, AlertCircle, BookOpen,
  Star, Users, Search, ChevronLeft, ChevronRight, Tag,
  RefreshCw, Info
} from 'lucide-react'
import toast from 'react-hot-toast'

const CATEGORIES = [
  'DevOps & Cloud Technologies',
  'RedHat Technologies',
  'Cisco & Microsoft Technologies',
  'Cyber Security Technologies',
  'Software Technologies',
  'Python & Data Science',
]
const STATUSES = ['active', 'inactive', 'draft']
const CURRENCIES = ['INR', 'USD', 'GBP', 'EUR', 'AED']

const empty = {
  title: '', slug: '', description: '', overview: '',
  category: CATEGORIES[0], subcategory: '',
  duration: '', price: '', discountedPrice: '',
  currency: 'INR', status: 'active',
  featured: false, popular: false,
  image: '',
  skills: '', prerequisites: '', tags: '',
  instructor: { name: '', bio: '', experience: '', linkedin: '' },
}

const fi = 'w-full px-3.5 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all'

const F = ({ label, hint, children, span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{label}</label>
      {hint && <span className="text-xs text-gray-400 dark:text-gray-500">{hint}</span>}
    </div>
    {children}
  </div>
)

const SectionTitle = ({ children }) => (
  <div className="col-span-2 flex items-center gap-3 pt-2">
    <span className="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">{children}</span>
    <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
  </div>
)

const arrToStr = (arr) => Array.isArray(arr) ? arr.join(', ') : (arr || '')
const strToArr = (str) => str.split(',').map(s => s.trim()).filter(Boolean)

const toSlug = (str) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')

const genSlug = toSlug

const genUniqueSlug = (title) => {
  const base = toSlug(title)
  const rand = Math.random().toString(36).slice(2, 6)
  return base ? `${base}-${rand}` : rand
}

const CourseModal = ({ course, onClose, onSaved }) => {
  const [form, setForm] = useState(course ? {
    ...course,
    discountedPrice: course.discountedPrice || '',
    skills: arrToStr(course.skills),
    prerequisites: arrToStr(course.prerequisites),
    tags: arrToStr(course.tags),
  } : { ...empty })
  const [saving, setSaving] = useState(false)
  const [slugEdited, setSlugEdited] = useState(!!course)

  const set = (path, val) => setForm(prev => {
    const next = { ...prev }
    const keys = path.split('.')
    let obj = next
    keys.slice(0, -1).forEach(k => { obj[k] = { ...obj[k] }; obj = obj[k] })
    obj[keys[keys.length - 1]] = val
    return next
  })

  const handleTitleChange = (val) => {
    set('title', val)
    if (!slugEdited) set('slug', genSlug(val))
  }

  const regenerateSlug = () => {
    set('slug', genUniqueSlug(form.title))
    setSlugEdited(true)
  }

  const save = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        discountedPrice: form.discountedPrice ? Number(form.discountedPrice) : undefined,
        skills: strToArr(form.skills),
        prerequisites: strToArr(form.prerequisites),
        tags: strToArr(form.tags),
      }
      if (course) await coursesAPI.update(course._id, payload)
      else await coursesAPI.create(payload)
      toast.success(course ? 'Course updated' : 'Course created')
      onSaved()
    } catch (err) {
      toast.error(err.error || err.message || 'Save failed')
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl my-6 border border-gray-200 dark:border-gray-800">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">{course ? 'Edit Course' : 'New Course'}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{course ? `Editing: ${course.title}` : 'Fill in the details below'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-400 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={save} className="p-6">
          <div className="grid grid-cols-2 gap-x-5 gap-y-4">

            <SectionTitle>Basic Info</SectionTitle>

            <F label="Course Title" span>
              <input required value={form.title} onChange={e => handleTitleChange(e.target.value)}
                placeholder="e.g. DevOps Engineering Masterclass"
                className={fi} />
            </F>

            <F label="URL Slug" hint="auto-generated · editable">
              <div className="relative">
                <input required value={form.slug} onChange={e => { set('slug', e.target.value); setSlugEdited(true) }}
                  placeholder="devops-engineering-masterclass"
                  className={`${fi} pr-10 font-mono text-xs`} />
                <button type="button" onClick={regenerateSlug}
                  title="Regenerate unique slug"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-500 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
            </F>

            <F label="Duration">
              <input required value={form.duration} onChange={e => set('duration', e.target.value)}
                placeholder="e.g. 3 months" className={fi} />
            </F>

            <SectionTitle>Category</SectionTitle>

            <F label="Category">
              <select value={form.category} onChange={e => set('category', e.target.value)} className={fi}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>

            <F label="Subcategory">
              <input required value={form.subcategory} onChange={e => set('subcategory', e.target.value)}
                placeholder="e.g. Kubernetes, AWS" className={fi} />
            </F>

            <SectionTitle>Pricing & Status</SectionTitle>

            <F label="Currency">
              <select value={form.currency} onChange={e => set('currency', e.target.value)} className={fi}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>

            <F label="Price">
              <input required type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)}
                placeholder="0" className={fi} />
            </F>

            <F label="Discounted Price" hint="optional">
              <input type="number" min="0" value={form.discountedPrice} onChange={e => set('discountedPrice', e.target.value)}
                placeholder="0" className={fi} />
            </F>

            <F label="Status">
              <select value={form.status} onChange={e => set('status', e.target.value)} className={fi}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </F>

            <F label="Visibility" span>
              <div className="flex items-center gap-6 mt-1">
                {[['featured', 'Featured'], ['popular', 'Popular']].map(([key, lbl]) => (
                  <label key={key} className="flex items-center gap-2.5 cursor-pointer group">
                    <div onClick={() => set(key, !form[key])}
                      className={`w-10 h-5 rounded-full transition-all relative cursor-pointer ${form[key] ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${form[key] ? 'left-5' : 'left-0.5'}`} />
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{lbl}</span>
                  </label>
                ))}
              </div>
            </F>

            <SectionTitle>Content</SectionTitle>

            <F label="Short Description" span>
              <textarea required rows={2} value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Brief summary shown in course listings…"
                className={`${fi} resize-none`} />
            </F>

            <F label="Full Overview" span>
              <textarea required rows={3} value={form.overview} onChange={e => set('overview', e.target.value)}
                placeholder="Detailed course overview…"
                className={`${fi} resize-none`} />
            </F>

            <F label="Course Image URL" hint="optional · shown in listings" span>
              <input type="url" value={form.image || ''} onChange={e => set('image', e.target.value)}
                placeholder="https://example.com/course-cover.jpg"
                className={fi} />
              {form.image && (
                <img
                  src={form.image}
                  alt="Course image preview"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                  onLoad={(e) => { e.currentTarget.style.display = 'block' }}
                  className="mt-2 h-28 w-auto rounded-lg border border-gray-200 dark:border-gray-700 object-cover"
                />
              )}
            </F>

            <F label="Skills" hint="comma-separated">
              <input value={form.skills} onChange={e => set('skills', e.target.value)}
                placeholder="Docker, Kubernetes, AWS, CI/CD" className={fi} />
            </F>

            <F label="Prerequisites" hint="comma-separated">
              <input value={form.prerequisites} onChange={e => set('prerequisites', e.target.value)}
                placeholder="Basic Linux, Networking" className={fi} />
            </F>

            <F label="Tags" hint="comma-separated">
              <input value={form.tags} onChange={e => set('tags', e.target.value)}
                placeholder="devops, cloud, aws" className={fi} />
            </F>

            <SectionTitle>Instructor</SectionTitle>

            <F label="Name">
              <input value={form.instructor?.name || ''} onChange={e => set('instructor.name', e.target.value)}
                placeholder="Instructor name" className={fi} />
            </F>

            <F label="Experience">
              <input value={form.instructor?.experience || ''} onChange={e => set('instructor.experience', e.target.value)}
                placeholder="e.g. 10 years in DevOps" className={fi} />
            </F>

            <F label="LinkedIn URL" span>
              <input value={form.instructor?.linkedin || ''} onChange={e => set('instructor.linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/username" className={fi} />
            </F>

            <F label="Bio" span>
              <textarea rows={2} value={form.instructor?.bio || ''} onChange={e => set('instructor.bio', e.target.value)}
                placeholder="Short bio…" className={`${fi} resize-none`} />
            </F>

          </div>

          <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-gray-100 dark:border-gray-800">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2 shadow-sm transition-all">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {course ? 'Save Changes' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const statusBadge = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
}

const AdminCourses = () => {
  const [courses, setCourses] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [page, setPage] = useState(1)
  const [modal, setModal] = useState(null)

  const fetchCourses = useCallback(async () => {
    setLoading(true)
    try {
      const res = await coursesAPI.getAll({
        page, limit: 12,
        all: 'true',
        category: filterCategory || undefined,
        search: search || undefined,
      })
      setCourses(res.data || [])
      setPagination(res.pagination || { page: 1, pages: 1, total: 0 })
    } catch { toast.error('Failed to load courses') }
    finally { setLoading(false) }
  }, [page, filterCategory, search])

  useEffect(() => { fetchCourses() }, [fetchCourses])

  const deleteCourse = async (id) => {
    if (!window.confirm('Delete this course? This cannot be undone.')) return
    try {
      await coursesAPI.delete(id)
      toast.success('Course deleted')
      fetchCourses()
    } catch { toast.error('Delete failed') }
  }

  return (
    <AdminLayout>
      {modal !== null && (
        <CourseModal
          course={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchCourses() }}
        />
      )}

      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">Courses</h1>
            <p className="text-sm text-gray-500 mt-1">{pagination.total} total courses</p>
          </div>
          <button onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm">
            <Plus className="w-4 h-4" /> New Course
          </button>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search courses…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1) }}
            className="px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 flex items-center justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-primary-600" />
            </div>
          ) : courses.length === 0 ? (
            <div className="col-span-3 py-16 text-center">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No courses found</p>
            </div>
          ) : courses.map((course) => (
            <div key={course._id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 flex items-center justify-center shrink-0">
                  <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex items-center gap-1.5">
                  {course.featured && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">Featured</span>}
                  {course.popular && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">Popular</span>}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge[course.status]}`}>{course.status}</span>
                </div>
              </div>

              <h3 className="font-bold text-gray-900 dark:text-white mt-3 leading-tight">{course.title}</h3>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{course.description}</p>

              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="px-2 py-0.5 rounded-full text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 flex items-center gap-1">
                  <Tag className="w-3 h-3" />{course.subcategory}
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{course.duration}</span>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-bold text-gray-900 dark:text-white">
                      {course.currency} {(course.discountedPrice || course.price)?.toLocaleString()}
                    </span>
                    {course.discountedPrice && (
                      <span className="text-xs text-gray-400 line-through">{course.currency} {course.price?.toLocaleString()}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{course.enrolledStudents || 0}</span>
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400" />{course.rating?.average?.toFixed(1) || '0.0'}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setModal(course)} className="p-2 rounded-xl text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteCourse(course._id)} className="p-2 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminCourses
